import { TOUCH_INPUT_TUNING } from "../config/input.ts";

export type MobileControlMode =
  | "grounded"
  | "flying"
  | "boating"
  | "driving"
  | "passenger";

export interface MobileControlsCallbacks {
  readonly onMove: (moveX: number, moveZ: number) => void;
  readonly onLookDelta: (deltaX: number, deltaY: number) => void;
  readonly onJumpHeld: (held: boolean) => void;
  readonly onDescendHeld: (held: boolean) => void;
  readonly onSprintHeld: (held: boolean) => void;
  readonly onInteract: () => void;
  readonly onToggleFlight: () => void;
  readonly onCycleSeat: () => void;
  readonly onEngage?: () => void;
}

export interface MobileControlsViewModel {
  readonly mode: MobileControlMode;
  readonly enabled: boolean;
}

export interface MobileControls {
  update(viewModel: Readonly<MobileControlsViewModel>): void;
  dispose(): void;
}

function createActionButton(
  label: string,
  className: string,
  ariaLabel: string,
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `mobile-action ${className}`;
  button.textContent = label;
  button.setAttribute("aria-label", ariaLabel);
  return button;
}

export function createMobileControls(
  root: HTMLElement,
  lookSurface: HTMLElement,
  callbacks: Readonly<MobileControlsCallbacks>,
): MobileControls {
  const controls = document.createElement("section");
  controls.className = "mobile-controls";
  controls.dataset.enabled = "true";
  controls.dataset.mode = "grounded";
  controls.setAttribute("aria-label", "Touch controls");

  const movement = document.createElement("div");
  movement.className = "mobile-movement";
  movement.setAttribute("role", "group");
  movement.setAttribute("aria-label", "Movement joystick");
  const movementRing = document.createElement("div");
  movementRing.className = "mobile-movement-ring";
  const movementKnob = document.createElement("div");
  movementKnob.className = "mobile-movement-knob";
  movementKnob.setAttribute("aria-hidden", "true");
  const movementLabel = document.createElement("span");
  movementLabel.className = "mobile-movement-label";
  movementLabel.textContent = "MOVE";
  movementRing.append(movementKnob, movementLabel);
  movement.append(movementRing);

  const actions = document.createElement("div");
  actions.className = "mobile-actions";
  const interact = createActionButton("USE", "mobile-action--primary", "Use or interact");
  const jump = createActionButton("JUMP", "mobile-action--jump", "Jump");
  const sprint = createActionButton("RUN", "mobile-action--sprint", "Hold to sprint");
  const descend = createActionButton("DOWN", "mobile-action--descend", "Hold to descend");
  const flight = createActionButton("FLY", "mobile-action--flight", "Toggle flight mode");
  const seat = createActionButton("SEAT", "mobile-action--seat", "Change vehicle seat");
  actions.append(sprint, descend, flight, seat, jump, interact);

  const lookHint = document.createElement("p");
  lookHint.className = "mobile-look-hint";
  lookHint.textContent = "Swipe to look";
  controls.append(movement, actions, lookHint);
  root.append(controls);

  let enabled = true;
  let movementPointerId: number | null = null;
  let lookPointerId: number | null = null;
  let lastLookX = 0;
  let lastLookY = 0;

  function updateMovement(clientX: number, clientY: number): void {
    const bounds = movementRing.getBoundingClientRect();
    const radius = TOUCH_INPUT_TUNING.joystickRadiusCssPixels;
    const rawX = clientX - (bounds.left + bounds.width / 2);
    const rawY = clientY - (bounds.top + bounds.height / 2);
    const distance = Math.hypot(rawX, rawY);
    const scale = distance > radius ? radius / distance : 1;
    const visualX = rawX * scale;
    const visualY = rawY * scale;
    movementKnob.style.transform = `translate(${visualX}px, ${visualY}px)`;
    callbacks.onMove(visualX / radius, -visualY / radius);
  }

  function releaseMovement(pointerId?: number): void {
    if (
      movementPointerId === null ||
      (pointerId !== undefined && pointerId !== movementPointerId)
    ) {
      return;
    }
    movementPointerId = null;
    movementKnob.style.transform = "translate(0, 0)";
    callbacks.onMove(0, 0);
  }

  const onMovementPointerDown = (event: PointerEvent): void => {
    if (!enabled || movementPointerId !== null) return;
    event.preventDefault();
    event.stopPropagation();
    callbacks.onEngage?.();
    movementPointerId = event.pointerId;
    movement.setPointerCapture(event.pointerId);
    updateMovement(event.clientX, event.clientY);
  };
  const onMovementPointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== movementPointerId) return;
    event.preventDefault();
    updateMovement(event.clientX, event.clientY);
  };
  const onMovementPointerEnd = (event: PointerEvent): void => {
    releaseMovement(event.pointerId);
  };
  movement.addEventListener("pointerdown", onMovementPointerDown);
  movement.addEventListener("pointermove", onMovementPointerMove);
  movement.addEventListener("pointerup", onMovementPointerEnd);
  movement.addEventListener("pointercancel", onMovementPointerEnd);
  movement.addEventListener("lostpointercapture", onMovementPointerEnd);

  const onLookPointerDown = (event: PointerEvent): void => {
    if (
      !enabled ||
      lookPointerId !== null ||
      (event.pointerType !== "touch" && event.pointerType !== "pen")
    ) {
      return;
    }
    event.preventDefault();
    callbacks.onEngage?.();
    lookPointerId = event.pointerId;
    lastLookX = event.clientX;
    lastLookY = event.clientY;
    lookSurface.setPointerCapture(event.pointerId);
  };
  const onLookPointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== lookPointerId) return;
    event.preventDefault();
    const deltaX = event.clientX - lastLookX;
    const deltaY = event.clientY - lastLookY;
    lastLookX = event.clientX;
    lastLookY = event.clientY;
    callbacks.onLookDelta(
      deltaX * TOUCH_INPUT_TUNING.lookDeltaScale,
      deltaY * TOUCH_INPUT_TUNING.lookDeltaScale,
    );
  };
  const onLookPointerEnd = (event: PointerEvent): void => {
    if (event.pointerId === lookPointerId) lookPointerId = null;
  };
  lookSurface.addEventListener("pointerdown", onLookPointerDown);
  lookSurface.addEventListener("pointermove", onLookPointerMove);
  lookSurface.addEventListener("pointerup", onLookPointerEnd);
  lookSurface.addEventListener("pointercancel", onLookPointerEnd);
  lookSurface.addEventListener("lostpointercapture", onLookPointerEnd);

  const holdBindings: Array<{
    button: HTMLButtonElement;
    callback: (held: boolean) => void;
    pointerId: number | null;
  }> = [
    { button: jump, callback: callbacks.onJumpHeld, pointerId: null },
    { button: sprint, callback: callbacks.onSprintHeld, pointerId: null },
    { button: descend, callback: callbacks.onDescendHeld, pointerId: null },
  ];
  for (const binding of holdBindings) {
    const release = (event: PointerEvent): void => {
      if (event.pointerId !== binding.pointerId) return;
      binding.pointerId = null;
      binding.button.dataset.held = "false";
      binding.callback(false);
    };
    binding.button.addEventListener("pointerdown", (event) => {
      if (!enabled || binding.pointerId !== null) return;
      event.preventDefault();
      event.stopPropagation();
      callbacks.onEngage?.();
      binding.pointerId = event.pointerId;
      binding.button.dataset.held = "true";
      binding.button.setPointerCapture(event.pointerId);
      binding.callback(true);
    });
    binding.button.addEventListener("pointerup", release);
    binding.button.addEventListener("pointercancel", release);
    binding.button.addEventListener("lostpointercapture", release);
  }

  const edgeBindings: ReadonlyArray<readonly [HTMLButtonElement, () => void]> = [
    [interact, callbacks.onInteract],
    [flight, callbacks.onToggleFlight],
    [seat, callbacks.onCycleSeat],
  ];
  for (const [button, callback] of edgeBindings) {
    button.addEventListener("click", (event) => {
      if (!enabled) return;
      event.preventDefault();
      event.stopPropagation();
      callbacks.onEngage?.();
      callback();
    });
  }

  function releaseHeldControls(): void {
    releaseMovement();
    lookPointerId = null;
    for (const binding of holdBindings) {
      if (binding.pointerId !== null) {
        binding.pointerId = null;
        binding.button.dataset.held = "false";
        binding.callback(false);
      }
    }
  }

  return {
    update(viewModel): void {
      if (enabled !== viewModel.enabled) {
        enabled = viewModel.enabled;
        controls.dataset.enabled = String(enabled);
        controls.setAttribute("aria-hidden", String(!enabled));
        if (!enabled) releaseHeldControls();
      }
      controls.dataset.mode = viewModel.mode;
      interact.textContent =
        viewModel.mode === "driving" ||
        viewModel.mode === "passenger" ||
        viewModel.mode === "boating"
          ? "EXIT"
          : "USE";
      jump.textContent = viewModel.mode === "flying" ? "RISE" : "JUMP";
      sprint.textContent = viewModel.mode === "flying" ? "FAST" : "RUN";
      flight.textContent = viewModel.mode === "flying" ? "LAND" : "FLY";
      descend.hidden = viewModel.mode !== "flying";
      seat.hidden =
        viewModel.mode !== "driving" && viewModel.mode !== "passenger";
      jump.hidden =
        viewModel.mode === "driving" ||
        viewModel.mode === "passenger" ||
        viewModel.mode === "boating";
      sprint.hidden =
        viewModel.mode === "driving" || viewModel.mode === "passenger";
    },
    dispose(): void {
      releaseHeldControls();
      movement.removeEventListener("pointerdown", onMovementPointerDown);
      movement.removeEventListener("pointermove", onMovementPointerMove);
      movement.removeEventListener("pointerup", onMovementPointerEnd);
      movement.removeEventListener("pointercancel", onMovementPointerEnd);
      movement.removeEventListener("lostpointercapture", onMovementPointerEnd);
      lookSurface.removeEventListener("pointerdown", onLookPointerDown);
      lookSurface.removeEventListener("pointermove", onLookPointerMove);
      lookSurface.removeEventListener("pointerup", onLookPointerEnd);
      lookSurface.removeEventListener("pointercancel", onLookPointerEnd);
      lookSurface.removeEventListener("lostpointercapture", onLookPointerEnd);
      controls.remove();
    },
  };
}
